import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRamadan } from "../context/RamadanContext";
import { useDailyContent } from "../utils/dataService";

const DailyContent: React.FC = () => {
  const { t } = useTranslation();
  const { currentDay } = useRamadan();
  const { dayData, updateSpecialAchievement } = useRamadan();
  const [activeTab, setActiveTab] = useState<"verse" | "hadith">("verse");
  
  // Use the new hook that handles translations with i18n
  // This will automatically update when language changes
  const content = useDailyContent(currentDay);

  if (!content) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div className="grid grid-cols-1 gap-1">
      {/* Mobile View - Tab Navigation */}
      <div className="md:hidden">
        {/* Tabs Navigation - Only visible on mobile */}
        <div className="grid grid-cols-2 gap-1">
          <button
            className={`${
              activeTab === "verse"
                ? "bg-[#a6ce86]"
                : "bg-[#d0e5c1] hover:bg-[#b8d99f]"
            } py-2 text-center font-bengali font-semibold text-white rounded-t-lg transition-colors duration-200`}
            onClick={() => setActiveTab("verse")}
          >
            {t('sections.verse')}
          </button>
          <button
            className={`${
              activeTab === "hadith"
                ? "bg-[#a6ce86]"
                : "bg-[#d0e5c1] hover:bg-[#b8d99f]"
            } py-2 text-center font-bengali font-semibold text-white rounded-t-lg transition-colors duration-200`}
            onClick={() => setActiveTab("hadith")}
          >
            {t('sections.hadith')}
          </button>
        </div>

        {/* Tab Content for Mobile - Only show active tab */}
        <div className="bg-[#e3f0d8] rounded-t-none rounded-b-lg p-4 border-t-0">
          {/* Verse of the Day Content */}
          {activeTab === "verse" && (
            <div className="space-y-3">
              <p
                className="text-right font-medium text-lg leading-relaxed text-gray-800 font-arabic"
                dir="rtl"
              >
                {content.verse.arabic}
              </p>
              <p className="font-bengali text-gray-700 mt-2">
                {t(content.verse.translationKey)}
              </p>
            </div>
          )}

          {/* Hadith of the Day Content */}
          {activeTab === "hadith" && (
            <div className="space-y-2">
              <p className="font-bengali text-gray-700">{t(content.hadith.textKey)}</p>
              <p className="font-bengali text-sm text-gray-500 italic">
                {t(content.hadith.referenceKey)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Desktop View - Both sections side by side */}
      <div className="hidden md:grid md:grid-cols-2 md:gap-1">
        {/* Left column - Verse section */}
        <div>
          {/* Verse Header */}
          <div className="bg-[#a6ce86] py-2 text-center font-bengali font-semibold text-white rounded-t-lg">
            {t('sections.verse')}
          </div>
          {/* Verse Content */}
          <div className="bg-[#e3f0d8] rounded-t-none rounded-b-lg p-4 border-t-0 h-full">
            <div className="space-y-3">
              <p
                className="text-right font-medium text-lg leading-relaxed text-gray-800 font-arabic"
                dir="rtl"
              >
                {content.verse.arabic}
              </p>
              <p className="font-bengali text-gray-700 mt-2">
                {t(content.verse.translationKey)}
              </p>
            </div>
          </div>
        </div>

        {/* Right column - Hadith section */}
        <div>
          {/* Hadith Header */}
          <div className="bg-[#a6ce86] py-2 text-center font-bengali font-semibold text-white rounded-t-lg">
            {t('sections.hadith')}
          </div>
          {/* Hadith Content */}
          <div className="bg-[#e3f0d8] rounded-t-none rounded-b-lg p-4 border-t-0 h-full">
            <div className="space-y-2">
              <p className="font-bengali text-gray-700">{t(content.hadith.textKey)}</p>
              <p className="font-bengali text-sm text-gray-500 italic">
                {t(content.hadith.referenceKey)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Special Achievement Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mt-2">
        <h2 className="text-lg font-bengali font-semibold text-[#6a8e50] border-b pb-2 mb-3">
          {t('sections.specialAchievement')}
        </h2>
        <div className="font-bengali bg-gray-50 p-3 rounded">
          <textarea
            value={dayData?.specialAchievement || ""  }
            onChange={(e) => updateSpecialAchievement(e.target.value)}
            className="w-full min-h-[100px] p-2 rounded border border-gray-200 text-[#6a8e50] font-medium font-bengali resize-y focus:outline-none focus:ring-1 focus:ring-[#6a8e50] focus:border-[#6a8e50]"
            placeholder={t('specialAchievement.placeholder')}
          />
        </div>
      </div>

      {/* Dua of the Day - Salmon/Pink card */}
      <div className="bg-[#f9e2df] rounded-lg p-4 mt-2">
        <h2 className="text-lg font-bengali font-semibold text-[#c27260] border-b pb-2 mb-3">
          {t('sections.dua')}
        </h2>
        <div className="space-y-3">
          <p
            className="text-right font-medium text-lg leading-relaxed text-gray-800 font-arabic"
            dir="rtl"
          >
            {content.dua.arabic}
          </p>
          <p className="font-bengali text-gray-700">
            {t(content.dua.translationKey)}
          </p>
        </div>
      </div>

      {/* Names of Allah - Bottom section with grid */}
      <div className="mt-2">
        <div className="border-t-4 border-b-4 border-[#f2e8c7] py-2 bg-[#f7f5e8]">
          <h2 className="text-lg font-bengali font-semibold text-center mb-2">
            {t('sections.namesOfAllah')}
          </h2>
          <div className="grid grid-cols-3 gap-0">
            {content.namesOfAllah.map((name, index) => (
              <div
                key={index}
                className="text-center p-2 border-r last:border-r-0"
              >
                <p className="text-xl font-bold mb-1 text-[#6a8e50] font-arabic" dir="rtl">
                  {name.arabic}
                </p>
                <p className="font-bengali text-gray-700 text-sm">
                  {t(name.meaningKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyContent;
